import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { services } from "@/data/services";
import { StickyWhatsApp, waLink } from "@/components/WhatsAppButton";
import { HeroBanner } from "@/components/HeroBanner";
import brandLogo from "@/assets/brandin-logo.png";
import heroSlide1 from "@/assets/hero-slide-1.jpg";
import heroSlide2 from "@/assets/hero-slide-2.jpg";
import heroSlide3 from "@/assets/hero-slide-3.jpg";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Branding, Web & WhatsApp Services — BrandIn Studio SA" },
      {
        name: "description",
        content:
          "Logo design, websites, WhatsApp Business setup, company profiles, posters, SEO and digital marketing services for small businesses in South Africa.",
      },
      { property: "og:title", content: "Branding, Web & WhatsApp Services — BrandIn Studio SA" },
      { property: "og:description", content: "Full-service branding, web design and digital marketing for South African small businesses." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.brandinstudio.co.za/services" },
    ],
    links: [{ rel: "canonical", href: "https://www.brandinstudio.co.za/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: services.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Service",
              name: s.title,
              description: s.desc,
              provider: { "@type": "LocalBusiness", name: "BrandIn Studio" },
              areaServed: "ZA",
            },
          })),
        }),
      },
    ],
  }),
  component: ServicesPage,
});

function ServicesPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img src={brandLogo} alt="BrandIn Studio" className="h-10 w-auto" />
          </Link>
          <Link to="/" className="text-sm font-medium text-muted-foreground hover:text-foreground">
            ← Back home
          </Link>
        </div>
      </header>

      <HeroBanner
        kicker={
          <>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            Services
          </>
        }
        title="Everything Your Brand Needs"
        titleHighlight="in One Studio"
        subtitle="From logo design to lead generation — practical services built for small businesses in South Africa that want to look credible and convert."
        primaryCta={{
          label: "Start a Brand Assessment",
          href: waLink("Hi BrandIn Studio, I'd like to discuss your services."),
          external: true,
        }}
        secondaryCta={{
          label: "See packages",
          href: "/#packages",
          variant: "secondary",
        }}
        images={[
          { src: heroSlide1, alt: "BrandIn Studio branded delivery van" },
          { src: heroSlide2, alt: "Stationery and brand identity kit" },
          { src: heroSlide3, alt: "Roll-up banner and signage" },
        ]}
        checks={["Logo & Identity", "Websites & Landing Pages", "WhatsApp Business Setup"]}
      />

      <section className="py-16 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <article
                key={s.title}
                className="group overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:border-gold hover:shadow-elegant"
              >
                <div className="aspect-[4/3] overflow-hidden bg-secondary">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    width={800}
                    height={600}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h2 className="text-lg font-bold leading-snug text-foreground">{s.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-secondary py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-6">
          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Not sure which service you need?
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            Send us a WhatsApp — we'll give you a free brand assessment and a clear recommendation.
          </p>
          <div className="mt-6">
            <a
              href={waLink("Hi BrandIn Studio, I'd like a free brand assessment.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-gold-foreground shadow-soft transition-transform hover:-translate-y-0.5"
            >
              Free Brand Assessment on WhatsApp <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <StickyWhatsApp />
    </div>
  );
}
